import React, { useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import {
  BubbleAlert,
  BubbleBadge,
  BubbleFormGroup,
  BubbleOverlay,
  BubbleSelect,
} from '@go-client/components/BubbleUI';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import { ReservableScheduleFormikForm } from '@go-client/components/Formik/ReservableScheduleFormik';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { findUpcomingByTemplate } from '@go-client/lib/api/reservation';
import { getSimpleUsers } from '@go-client/lib/api/user';
import { FlattenedReservation, RemapReservationSlot } from '@go-shared/types/reservation';
import { ReserveTimelineItem } from '@go-shared/types/reserve';
import { BareReserveSchedule } from '@go-shared/types/reserveSchedule';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { SimpleUser } from '@go-shared/types/user';
import {
  timeToString,
  addMinutesToTime,
  DEFAULT_TIMESTR_FORMAT,
  dateToIsoUTC,
  timeToMinutes,
  getLocaleDateStringFromIso,
  findMinMaxDate,
} from '@go-shared/lib/time';
import { getHourFormat } from '@go-shared/lib/property';
import { makeFullName } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import { RESERVABLE_SCHEDULE_BREADCRUMBS } from '../ReservableSchedulePath';
import {
  buildNeedsAttentionItems,
  buildReserveTimeline,
  buildReserveTimeslotLabel,
} from '@go-shared/lib/reserve';
import { EventTemplateFormikForm } from '@go-client/components/Formik/EventTemplateFormik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface SlotMappingPagePropsBase {
  scheduleFormik: FormikProps<ReservableScheduleFormikForm>;
  eventFormik: FormikProps<EventTemplateFormikForm>;
  isBusy: boolean;
  setIsBusy: (v: boolean) => void;
  remaps: RemapReservationSlot[];
  setRemaps: (newMaps: RemapReservationSlot[]) => void;
  onGoBack?: () => void;
  onClose: () => void;
  scheduleLocked: boolean;
}

type SlotMappingPageProps = SlotMappingPagePropsBase &
  FormPageComponentHelpfulProps<RESERVABLE_SCHEDULE_BREADCRUMBS>;

interface FloatingSlotRow {
  id: string;
  date: string;
  currentTime: string;
  currentEndTime?: string;
  currentHostId?: string;
  currentRentalId?: string;
  reservation: FlattenedReservation;
  availableSlots: ReserveTimelineItem[];
  selectedSlot: ReserveTimelineItem | undefined;
}

function findClosestTimelineItem(
  reservation: FlattenedReservation,
  items: ReserveTimelineItem[]
): ReserveTimelineItem | undefined {
  const consideredItems = items.filter(
    i =>
      i.rental === reservation.rental &&
      i.host === reservation.host &&
      i.dayspan === reservation._totalOccurrences
  );

  if (consideredItems.length === 0) return undefined;

  const tMin = timeToMinutes(reservation.time);

  return consideredItems.reduce((best, item) => {
    const diff = Math.abs(timeToMinutes(item.time) - tMin);
    const bestDiff = Math.abs(timeToMinutes(best.time) - tMin);
    return diff < bestDiff ? item : best;
  }, consideredItems[0]);
}

interface SlotDetailProps {
  date?: string;
  time: string;
  endTime?: string;
  hostName?: string;
  rentalName?: string;
  dayspan: number;
}

function SlotDetail(props: SlotDetailProps) {
  const { date, time, endTime, hostName, rentalName, dayspan } = props;

  return (
    <dl className="text-sm space-y-0.5">
      {date && (
        <div className="flex gap-2">
          <dt className="text-slate w-16 shrink-0">Date:</dt>
          <dd className="font-medium">{date}</dd>
        </div>
      )}
      <div className="flex gap-2">
        <dt className="text-slate w-16 shrink-0">Time:</dt>
        <dd className="font-medium">
          {time}
          {endTime && <> – {endTime}</>}
        </dd>
      </div>
      {hostName && (
        <div className="flex gap-2">
          <dt className="text-slate w-16 shrink-0">Host:</dt>
          <dd>{hostName}</dd>
        </div>
      )}
      {rentalName && (
        <div className="flex gap-2">
          <dt className="text-slate w-16 shrink-0">Rental:</dt>
          <dd>{rentalName}</dd>
        </div>
      )}
      <div className="flex gap-2">
        <dt className="text-slate w-16 shrink-0">Dayspan:</dt>
        <dd>{dayspan} days</dd>
      </div>
    </dl>
  );
}

export default function SlotMappingPage(props: SlotMappingPageProps) {
  const { activeProperty: property } = useActiveProperty();
  const hourFormat = getHourFormat(property);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<FloatingSlotRow[]>([]);
  const [usersDict, setUsersDict] = useState<Record<string, SimpleUser>>({});

  const { scheduleFormik, eventFormik } = props;

  const ticketsDict = mapBy(eventFormik.values.reservableTickets ?? [], t => t._id);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [scheduleFormik.values.template, property._id]);

  async function fetchData() {
    setLoading(true);

    const simulatedSchedule = (await scheduleFormik.submitForm()) as unknown as BareReserveSchedule;
    const simulatedEventTemplate = (await eventFormik.submitForm()) as unknown as BareEventTemplate;

    try {
      const [reservationsFromApi, users] = await Promise.all([
        findUpcomingByTemplate(property._id, simulatedSchedule.eventTemplate),
        getSimpleUsers(property._id),
      ]);

      setUsersDict(mapBy(users, '_id'));

      const reservations = reservationsFromApi.sort((a, b) => {
        const dateDiff = dateToIsoUTC(a.date).localeCompare(dateToIsoUTC(b.date));
        if (dateDiff !== 0) return dateDiff;
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      });

      if (reservations.length === 0) {
        setRows([]);
        return;
      }

      const [fromDate, toDate] = findMinMaxDate(
        ...reservations.flatMap(r => [r.date, ...(r.additionalOccurrences ?? []).map(o => o.date)])
      );

      const timelines =
        fromDate && toDate
          ? buildReserveTimeline(
              dateToIsoUTC(fromDate),
              dateToIsoUTC(toDate),
              [simulatedSchedule],
              [simulatedEventTemplate],
              reservations,
              true
            )
          : [];

      const newSlotsByDate: Record<string, ReserveTimelineItem[]> = {};
      for (const tl of timelines) {
        // tl.events.flatmap is safe here because buildReserveTimeline is done on one event only
        newSlotsByDate[tl.date] = tl.events.flatMap(e => e.items).filter(i => !i.isFloatingSlot);
      }

      const mapped: FloatingSlotRow[] = buildNeedsAttentionItems(timelines)
        .filter(item => item.type === 'unassigned')
        .flatMap(item => {
          const available = newSlotsByDate[item.date] ?? [];
          const dateStr = getLocaleDateStringFromIso(item.date, {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
            year: 'numeric',
          });

          return item.reservations.map(r => ({
            id: r._id,
            date: dateStr,
            currentTime: timeToString(r.time, hourFormat, DEFAULT_TIMESTR_FORMAT),
            currentEndTime: r.duration
              ? timeToString(
                  addMinutesToTime(r.time, r.duration),
                  hourFormat,
                  DEFAULT_TIMESTR_FORMAT
                )
              : undefined,
            currentHostId: r.host,
            currentRentalId: r.rental,
            reservation: r,
            availableSlots: available,
            selectedSlot: findClosestTimelineItem(r, available),
          }));
        });

      setRows(mapped);
    } finally {
      setLoading(false);
    }
  }

  function updateRow(index: number, selectedSlot: ReserveTimelineItem | undefined) {
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], selectedSlot };
      return next;
    });
  }

  function buildRemapsFromRows(): RemapReservationSlot[] {
    return rows.flatMap(row => {
      if (!row.selectedSlot) return [];
      const r = row.reservation;
      return [
        {
          reservationId: r._isOccurrenceOf ?? r._id,
          occurrenceId: r._isOccurrenceOf ? r._id : undefined,
          newTime: row.selectedSlot.time,
          newDuration: row.selectedSlot.duration,
          newHost: row.selectedSlot.host,
          newRental: row.selectedSlot.rental,
        },
      ];
    });
  }

  useEffect(() => {
    props.setRemaps(buildRemapsFromRows());
  }, [rows]);

  const handleGoForward = props.onGoForward
    ? () => {
        props.setRemaps(buildRemapsFromRows());
        props.onGoForward?.();
      }
    : undefined;

  function resolveHostName(hostId?: string): string | undefined {
    if (!hostId) return undefined;
    const u = usersDict[hostId];
    return u ? makeFullName(u.firstName, u.lastName) : undefined;
  }

  function resolveRentalName(rentalId?: string): string | undefined {
    if (!rentalId) return undefined;
    const r = ticketsDict[rentalId];
    return r ? r.name : undefined;
  }

  const hasRows = loading ? undefined : rows.length > 0;

  return (
    <div className="eaf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate mr-auto"
              items={[{ label: 'Experience Selected', value: props.eventFormik.values.title }]}
            />
            <div className="justify-items-end">
              <BubbleFormActions
                isBusy={props.isBusy}
                forwardTitle={props.forwardTitle || 'Finish: Create Experience'}
                backTitle={props.backTitle}
                onGoForward={handleGoForward}
                onGoBack={props.onGoBack}
              />
              {props.forwardTooltip && (
                <div className="text-right text-slate italic basis-full mt-1">
                  {props.forwardTooltip}
                </div>
              )}
            </div>
          </>
        )}
      >
        <BubbleFormHeader
          title={props.title ?? ''}
          subtitle="Remap Reservations"
          pageIndex={props.pageIndex}
          breadcrumbs={props.breadcrumbs}
          activeBreadcrumb="slotMapping"
          onBreadcrumbClick={props.onBreadcrumbClick}
          onClose={props.onClose}
        />
        {props.scheduleLocked && (
          <BubbleAlert className="mb-3" variant="ocean">
            <span className="font-semibold">This schedule cannot be modified. </span>
            You may not be a host for any of the schedule rules or you lack reserve admin
            permissions.
          </BubbleAlert>
        )}
        <BubbleOverlay loading={loading} loadingMinHeight="small">
          {hasRows !== undefined && !hasRows && (
            <BubbleAlert variant="lime" className="mb-4">
              No reservations need remapping — all upcoming bookings are already compatible with the
              new schedule.
            </BubbleAlert>
          )}
          {hasRows && (
            <div className="space-y-4">
              <BubbleAlert variant="banana">
                The following time slots no longer exist in the updated schedule. Select a new slot
                for each to map the reservations.
              </BubbleAlert>
              {rows.map((row, index) => (
                <div
                  key={row.id}
                  className="bg-white shadow-lg rounded-lg p-4 grid grid-cols-1 gap-4"
                >
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-navy font-bold">{row.reservation.reservationCode}</span>
                      {row.reservation._isOccurrenceOf && (
                        <BubbleBadge variant="ocean">Occurrence</BubbleBadge>
                      )}
                    </div>
                    <div className="flex items-start gap-6">
                      <div className="flex-1 min-w-0">
                        <BubbleFormGroup label="Current Slot" labelClassName="text-crimson">
                          <SlotDetail
                            date={row.date}
                            time={row.currentTime}
                            endTime={row.currentEndTime}
                            hostName={resolveHostName(row.currentHostId)}
                            dayspan={row.reservation._totalOccurrences}
                            rentalName={resolveRentalName(row.currentRentalId)}
                          />
                        </BubbleFormGroup>
                      </div>
                      <div className="flex items-center">
                        <FontAwesomeIcon icon="arrow-right" className="text-slate text-xl" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <BubbleFormGroup label="Remap To">
                          <BubbleSelect
                            allowUndefined
                            value={row.selectedSlot}
                            options={row.availableSlots}
                            labelExtractor={s =>
                              buildReserveTimeslotLabel({
                                ...s,
                                host: resolveHostName(s.host),
                                rental: resolveRentalName(s.rental),
                              })
                            }
                            readOnly={props.scheduleLocked}
                            undefinedLabel="Keep as-is (no change)"
                            onValue={val => updateRow(index, val)}
                          />
                        </BubbleFormGroup>
                        {row.selectedSlot && (
                          <div className="mt-2">
                            <SlotDetail
                              time={timeToString(
                                row.selectedSlot.time,
                                hourFormat,
                                DEFAULT_TIMESTR_FORMAT
                              )}
                              endTime={
                                row.selectedSlot.duration
                                  ? timeToString(
                                      addMinutesToTime(
                                        row.selectedSlot.time,
                                        row.selectedSlot.duration
                                      ),
                                      hourFormat,
                                      DEFAULT_TIMESTR_FORMAT
                                    )
                                  : undefined
                              }
                              hostName={resolveHostName(row.selectedSlot.host)}
                              rentalName={
                                row.selectedSlot.rental
                                  ? (ticketsDict[row.selectedSlot.rental]?.name ??
                                    row.selectedSlot.rental)
                                  : undefined
                              }
                              dayspan={row.selectedSlot.dayspan}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BubbleOverlay>
      </FormGenericContainer>
    </div>
  );
}
